import { Ref } from '@vue/composition-api';

/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { queryCreditApplyStatus } from '@/api/rights/shop-mall';
import { InstallMallCreditStatusEnum } from '@/api/rights/shop-mall/types';

/**
 * 轮询配置
 */
export const POLLING_CONFIG = {
  /** 轮询间隔：1秒 */
  interval: 1000,
};

/**
 * 轮询结果状态（最终结果）
 */
export enum PollingResultStatus {
  /** 成功 */
  SUCCESS = 'SUCCESS',
  /** 失败 */
  FAILED = 'FAILED',
  /** 超时 */
  TIMEOUT = 'TIMEOUT',
}

/**
 * 轮询结果
 */
export interface PollingResult {
  /** 状态 */
  status: PollingResultStatus;
   /** 授信任务单号 */
  creditTaskId: string;
  /** 授信状态（成功或失败时才有） */
  creditStatus?: InstallMallCreditStatusEnum;
  /** 商品ID列表 */
  pollingProductId?: string;
  /** 跳转地址 */
  jumpUrl?: string;
}

/**
 * 轮询回调函数类型
 */
export interface PollingCallbacks {
  /** 最终结果回调，只返回成功、失败、超时三种状态 */
  onResult: (result: PollingResult) => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
}

/**
 * 创建轮询函数
 */
export function createCreditPolling(
  pollingTimer: Ref<number | null>,
  creditTaskId: string,
  from: string,
  callbacks: PollingCallbacks,
): {
  startPolling: () => void;
  stopPolling: () => void;
} {
  /**
   * 停止轮询
   */
  const stopPolling = () => {
    if (pollingTimer.value) {
      clearInterval(pollingTimer.value);
      pollingTimer.value = null;
    }
  };

  /**
   * 检查授信状态
   */
  const checkCreditStatus = async (): Promise<void> => {
    try {
      const result = await queryCreditApplyStatus({
        creditTaskId,
        from,
      });

      if (result) {
        const { creditFrontStatus, productIdList = [] } = result;

        // 成功状态
        if (creditFrontStatus === InstallMallCreditStatusEnum.CREDIT_SUCCESS) {
          stopPolling();
          callbacks.onResult({
            status: PollingResultStatus.SUCCESS,
            creditTaskId,
            creditStatus: creditFrontStatus,
            pollingProductId: productIdList?.join(',') || '',
            jumpUrl: result?.jumpUrl || '',
          });
          return;
        }

        // 失败状态：拒绝、提交失败、过期（过期相当于拒绝）
        if (
          creditFrontStatus === InstallMallCreditStatusEnum.CREDIT_FAIL
        ) {
          stopPolling();
          callbacks.onResult({
            status: PollingResultStatus.FAILED,
            creditTaskId,
            creditStatus: creditFrontStatus,
            pollingProductId: productIdList?.join(',') || '',
          });
          return;
        }

        // 继续轮询状态：INIT、SUBMITTING、AUTHING
        // 这些状态在内部继续轮询，不通知外部
      }
    } catch (error) {
      // 错误不中断轮询，继续下一次
      if (callbacks.onError) {
        callbacks.onError(error as Error);
      }
    }
  };

  /**
   * 开始轮询
   */
  const startPolling = () => {
    pollingTimer.value = window.setInterval(() => {
      checkCreditStatus();
    }, POLLING_CONFIG.interval);

    // 立即执行一次
    checkCreditStatus();
  };

  return {
    startPolling,
    stopPolling,
  };
}
